import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchBusinessTrips } from "services/business-trip.service";
import { BusinessTrip } from "types/BusinessTrip";
import { AppDispatch, RootState } from "configureStore";

export const BUSINESS_TRIPS_PAGE_SIZE = 25;

interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
}

interface BusinessTripsState {
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  errMsg: string;
  collection: BusinessTrip[];
  pagination: Pagination | null;
  filtersValue: any;
  // Generation token bumped on every fresh page-1 load. Load-more requests
  // capture it and discard their response if it changed mid-flight.
  requestId: number;
}

const initialState: BusinessTripsState = {
  isLoading: true,
  isLoadingMore: false,
  isError: false,
  errMsg: "",
  collection: [],
  pagination: null,
  filtersValue: {},
  requestId: 0,
};

// Normalize the fetch response: the endpoint returns a bare array when called
// without pagination, or `{ businessTripsCollection, pagination }` when paged.
const normalize = (
  data: any
): { collection: BusinessTrip[]; pagination: Pagination | null } => {
  if (Array.isArray(data)) {
    return { collection: data, pagination: null };
  }
  return {
    collection: data?.businessTripsCollection || [],
    pagination: data?.pagination || null,
  };
};

const BusinessTripsSlice = createSlice({
  name: "businessTrips",
  initialState,
  reducers: {
    setDestroy() {
      return initialState;
    },
    setLoading(state: BusinessTripsState, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
      // Starting a fresh load clears any previous error so the UI can recover.
      if (action.payload) {
        state.isError = false;
        state.errMsg = "";
        // Supersede any in-flight load-more so its response is discarded.
        state.requestId += 1;
      }
    },
    setLoadingMore(state: BusinessTripsState, action: PayloadAction<boolean>) {
      state.isLoadingMore = action.payload;
    },
    setError(
      state: BusinessTripsState,
      action: PayloadAction<{ error: string }>
    ) {
      state.isLoading = false;
      state.isLoadingMore = false;
      state.isError = true;
      state.errMsg = action.payload.error;
    },
    setData(
      state: BusinessTripsState,
      action: PayloadAction<{
        collection: BusinessTrip[];
        pagination: Pagination | null;
      }>
    ) {
      state.collection = action.payload.collection;
      state.pagination = action.payload.pagination;
      state.isLoading = false;
      // A fresh page-1 result replaces the list, so any in-flight load-more
      // is now obsolete — clear its spinner instead of leaving it stuck.
      state.isLoadingMore = false;
    },
    appendData(
      state: BusinessTripsState,
      action: PayloadAction<{
        collection: BusinessTrip[];
        pagination: Pagination | null;
      }>
    ) {
      const seen = new Set(state.collection.map((t: any) => t.id));
      const fresh = action.payload.collection.filter(
        (t: any) => !seen.has(t.id)
      );
      state.collection = [...state.collection, ...fresh];
      state.pagination = action.payload.pagination;
      state.isLoadingMore = false;
    },
    setFilterValue(state, action) {
      const { key, value } = action.payload;
      state.filtersValue[key] = value;
    },
  },
});

const { actions, reducer } = BusinessTripsSlice;

export const {
  setDestroy,
  setLoading,
  setLoadingMore,
  setError,
  setData,
  appendData,
  setFilterValue,
} = actions;
export default reducer;

// First page (replace). Called on mount, on filter change, on retry, and after
// create/edit to refresh the list.
export const fetchBusinessTripsThunk =
  (
    companyId: string,
    filtersValue: any = {},
    page: number = 1,
    pageSize: number = BUSINESS_TRIPS_PAGE_SIZE
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setLoading(true));
    const requestId = getState().businessTripsReducer.requestId;
    try {
      const data = await fetchBusinessTrips(companyId, filtersValue, {
        page,
        pageSize,
      });
      if (getState().businessTripsReducer.requestId !== requestId) return;
      dispatch(setData(normalize(data)));
    } catch (e) {
      if (getState().businessTripsReducer.requestId !== requestId) return;
      const error = "not_fetch";
      dispatch(setError({ error }));
    }
  };

// Subsequent pages (append) for infinite scroll.
export const fetchMoreBusinessTrips =
  (
    companyId: string,
    filtersValue: any,
    page: number,
    pageSize: number = BUSINESS_TRIPS_PAGE_SIZE
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    // Capture the current generation; a fresh page-1 load (filter change /
    // refresh) bumps it and invalidates this response.
    const requestId = getState().businessTripsReducer.requestId;
    dispatch(setLoadingMore(true));
    try {
      const data = await fetchBusinessTrips(companyId, filtersValue, {
        page,
        pageSize,
      });
      if (getState().businessTripsReducer.requestId !== requestId) return;
      dispatch(appendData(normalize(data)));
    } catch (e) {
      if (getState().businessTripsReducer.requestId !== requestId) return;
      dispatch(setLoadingMore(false));
    }
  };
